import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MONO, eur, eurS } from './format'
import type { MoverDetail, MoverFilter } from '../types'
import type { MoverRow } from './aggregate'

interface MoversListProps {
  movers: MoverRow[]
  filter: MoverFilter
  /** Keyed by hierarchy id (MoverRow.key). */
  details: Record<string, MoverDetail>
  priorSpan: string
  currSpan: string
}

/** "Where the money moved" — replicates the live dashboard: a coloured left
 *  accent per row, tag + bold title, description, source line, the € value and a
 *  chevron; on expand, a "What drove it" table with prior / current / Δ columns
 *  whose headers follow the selected range. */
export function MoversList({ movers, filter, details, priorSpan, currSpan }: MoversListProps) {
  const [open, setOpen] = useState<string | null>(null)
  const rows = movers.filter((m) => filter === 'All' || (filter === 'Declines' ? m.dir === 'decline' : m.dir === 'gain'))
  if (!rows.length) {
    return <p className="py-6 text-center text-xs text-stone-400">No {filter.toLowerCase()} in this period.</p>
  }
  return (
    <div className="divide-y divide-stone-100 dark:divide-stone-800">
      {rows.map((m) => {
        const det = details[m.key]
        const isOpen = open === m.key
        const decline = m.dir === 'decline'
        return (
          <div
            key={m.key}
            className={cn(
              'border-l-2 transition-colors',
              decline ? 'border-red-400 dark:border-red-500/60' : 'border-emerald-400 dark:border-emerald-500/60',
              isOpen ? 'bg-[#F4EDDD] dark:bg-champagne-500/10' : 'hover:bg-[#F4EDDD] dark:hover:bg-champagne-500/10',
            )}
          >
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : m.key)}
              aria-expanded={isOpen}
              className="flex w-full items-start gap-3 px-4 py-3 text-left"
            >
              <span className={cn('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', decline ? 'bg-red-500' : 'bg-emerald-500')} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 dark:bg-stone-800 dark:text-stone-400">
                    {m.name}
                  </span>
                  <span
                    className={cn(
                      'rounded px-1.5 py-0.5 text-[10px] font-semibold',
                      decline ? 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300' : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
                    )}
                  >
                    {m.pct > 0 ? '+' : ''}{m.pct}% vs prior
                  </span>
                </div>
                {det && <p className="mt-1.5 text-xs leading-relaxed text-stone-500 dark:text-stone-400">{det.why}</p>}
              </div>
              <span className={cn('shrink-0 text-sm font-semibold', MONO, decline ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400')}>
                {eurS(m.delta)}
              </span>
              <ChevronDown className={cn('mt-0.5 h-4 w-4 shrink-0 text-stone-400 transition-transform', isOpen && 'rotate-180')} />
            </button>

            {isOpen && (
              <div className="px-4 pb-3 sm:pl-9">
                {det ? (
                  <>
                    {/* Mobile: stacked cards — the 4-column table collides at narrow widths */}
                    <div className="space-y-2 sm:hidden">
                      {det.lines.map((l, i) => {
                        const d = l.curr - l.prev
                        const up = d >= 0
                        return (
                          <div key={i} className="rounded-lg border border-stone-100 bg-stone-50/50 p-2.5 dark:border-stone-800 dark:bg-stone-800/30">
                            <div className="flex items-start justify-between gap-3">
                              <span className="text-sm font-medium text-stone-700 dark:text-stone-200">{l.name}</span>
                              <span className={cn('shrink-0 text-sm font-semibold', MONO, up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
                                {d === 0 ? '—' : eurS(d)}
                              </span>
                            </div>
                            <div className="mt-2 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-xs">
                              <span className="text-stone-400">{priorSpan}</span>
                              <span className={cn('text-right text-stone-500 dark:text-stone-400', MONO)}>{eur(l.prev)}</span>
                              <span className="text-stone-400">{currSpan}</span>
                              <span className={cn('text-right text-stone-700 dark:text-stone-200', MONO)}>{eur(l.curr)}</span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    {/* Desktop: full comparison table */}
                    <table className="hidden w-full text-sm sm:table">
                      <thead>
                        <tr className="border-y border-stone-100 text-[10px] uppercase tracking-wider text-stone-400 dark:border-stone-800">
                          <th className="py-1.5 text-left font-medium">What drove it</th>
                          <th className="py-1.5 text-right font-medium">{priorSpan}</th>
                          <th className="py-1.5 text-right font-medium">{currSpan}</th>
                          <th className="py-1.5 text-right font-medium">Δ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {det.lines.map((l, i) => {
                          const d = l.curr - l.prev
                          const up = d >= 0
                          return (
                            <tr key={i} className="border-b border-stone-50 last:border-0 hover:bg-champagne-50/60 dark:border-stone-800/60 dark:hover:bg-champagne-500/10">
                              <td className="py-1.5 pr-3 text-stone-700 dark:text-stone-200">{l.name}</td>
                              <td className={cn('py-1.5 text-right text-stone-400', MONO)}>{eur(l.prev)}</td>
                              <td className={cn('py-1.5 text-right text-stone-600 dark:text-stone-300', MONO)}>{eur(l.curr)}</td>
                              <td className={cn('py-1.5 text-right font-medium', MONO, up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
                                {d === 0 ? '—' : eurS(d)}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </>
                ) : (
                  <p className="py-2 text-xs text-stone-400">No detailed breakdown for this line.</p>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
